import { UserContext } from './UserContext';
import React, { useState, useEffect, useRef } from 'react';
import '../styles/Filelist.css';
import { useContext } from 'react';
import BatchAudioPlayer from './BatchAudioPlayer';

const FileList = () => {
  const [files, setFiles] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState('1M45Adlp6w_vPtL_nBKkBKtiDPDBAQ8el');
  const [folderHistory, setFolderHistory] = useState([]);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [audioUrl, setAudioUrl] = useState(null);
  const [progressData, setProgressData] = useState({});
  const [audioDuration, setAudioDuration] = useState(null);
  const [repeats, setRepeats] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioTexts, setAudioTexts] = useState({});
  const [currentPlayingFile, setCurrentPlayingFile] = useState(null);
  const [batchPlayingFile, setBatchPlayingFile] = useState(null);
  const [isLoadingText, setIsLoadingText] = useState(false);
  const [textFetchQueue, setTextFetchQueue] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [currentRepetition, setCurrentRepetition] = useState(1);
  const [isManualPlay, setIsManualPlay] = useState(false);
  const [audioGap, setAudioGap] = useState(2);
  const [folderStats, setFolderStats] = useState({
    audioCount: 0,
    folderCount: 0,
    totalFiles: 0
  });
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [tempAudioUrl, setTempAudioUrl] = useState(null);

  const audioRef = useRef(null);
  const playTimeRef = useRef(0);
  const playStartTimeRef = useRef(null);
  const { user } = useContext(UserContext);
  const mediaRecorderRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      const handleLoadedMetadata = () => {
        setAudioDuration(audioRef.current.duration);
      };

      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
      };
    }
  }, [audioRef.current]);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching files from folder:', currentFolderId);
        
        const response = await fetch(`https://gnrl-bknd-server.onrender.com/api/getFiles?folderId=${currentFolderId}`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Server error: ${errorData.message || response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Files received:', data.files);
        
        if (!data.files || data.files.length === 0) {
          setError('No files found in this folder');
          setFiles([]);
        } else {
          setFiles(data.files);
          
          if (user) {
            const audioFiles = data.files.filter(file => file.mimeType.startsWith('audio/'));
            await Promise.all(audioFiles.map(file => fetchProgress(file.id)));
          }
        }
      } catch (error) {
        console.error('Error fetching files:', error);
        setError(`Failed to load files: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFiles();
  }, [currentFolderId, user]);

  useEffect(() => {
    if (files.length > 0) {
      const stats = files.reduce((acc, file) => {
        if (file.mimeType === 'application/vnd.google-apps.folder') {
          acc.folderCount++;
        } else if (file.mimeType.startsWith('audio/')) {
          acc.audioCount++;
        }
        acc.totalFiles++;
        return acc;
      }, { audioCount: 0, folderCount: 0, totalFiles: 0 });
      
      setFolderStats(stats);
    }
  }, [files]);

  const fetchProgress = async (fileId) => {
    try {
      const file = files.find(f => f.id === fileId);
      if (!file || !user) return;

      const response = await fetch(`https://gnrl-bknd-server.onrender.com/api/progress/${user}/${encodeURIComponent(file.name)}`);
      if (response.ok) {
        const progress = await response.json();
        setProgressData(prev => ({
          ...prev,
          [fileId]: progress
        }));
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const updateProgress = async (fileId, updates) => {
    if (!user) return null;
  
    try {
      const file = files.find(f => f.id === fileId);
      if (!file) return null;

      const progressUpdate = {
        username: user,
        fileName: file.name,
        completed: updates.completed === true,
        repeats: updates.repeats || 1,
        playTime: Math.max(0, Math.round(updates.playTime || 0)),
        isFromBatch: Boolean(updates.isFromBatch)
      };

      console.log('FileList sending to server:', progressUpdate);

      const response = await fetch('https://gnrl-bknd-server.onrender.com/api/progress/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progressUpdate),
      });

      if (!response.ok) throw new Error('Failed to update progress');

      const result = await response.json();
      console.log('FileList received from server:', result);
      
      // Create the updated progress object
      const updatedProgress = {
        ...result,
        completed: result.completed === true,
        id: fileId // Add the fileId to match what BatchAudioPlayer expects
      };

      // Update local state
      setProgressData(prev => ({
        ...prev,
        [fileId]: updatedProgress
      }));

      return updatedProgress; // Return the complete object with fileId
    } catch (error) {
      console.error('Error updating progress:', error);
      return null;
    }
  };

  useEffect(() => {
    if (audioRef.current && previewFileId) {
      const audio = audioRef.current;
      let repetitionCount = 0;
      
      const handlePlay = () => {
        playStartTimeRef.current = Date.now();
        playTimeRef.current = progressData[previewFileId]?.playTime || 0;
        setIsPlaying(true);
      };
  
      const handlePause = async () => {
        setIsPlaying(false);
        if (playStartTimeRef.current) {
          const playDuration = (Date.now() - playStartTimeRef.current) / 1000;
          const newPlayTime = playTimeRef.current + playDuration;
          playTimeRef.current = newPlayTime;
          
          await updateProgress(previewFileId, {
            playTime: Number(newPlayTime.toFixed(2)),
            completed: newPlayTime === audio.duration
          });
          
          playStartTimeRef.current = null;
        }
      };
  
      const handleEnded = async () => {
        repetitionCount++;
        
        if (repetitionCount < repeats) {
          // If we haven't reached the desired number of repetitions, play again
          audio.currentTime = 0;
          audio.play();
        } else {
          // If we've completed all repetitions
          setIsPlaying(false);
          repetitionCount = 0;
          const actualDuration = Number(audio.duration.toFixed(2));
          playTimeRef.current = actualDuration;
          
          await updateProgress(previewFileId, {
            playTime: actualDuration,
            repeats: (progressData[previewFileId]?.repeats || 0) + 1,
            completed: true
          });
          
          playStartTimeRef.current = null;
        }
      };
  
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
  
      return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioRef.current, previewFileId, progressData, repeats]);

  const fetchAudioText = async (fileId) => {
    if (!fileId) return;

    // Implement rate limiting (wait at least 1 second between requests)
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    if (timeSinceLastFetch < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastFetch));
    }

    try {
      setIsLoadingText(true);
      
      // Check if we already have the text cached
      if (audioTexts[fileId]) {
        return;
      }

      const response = await fetch(`https://gnrl-bknd-server.onrender.com/api/audio-text/${fileId}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAudioTexts(prev => ({
        ...prev,
        [fileId]: data.text || 'No transcription available.'
      }));
      setLastFetchTime(Date.now());

    } catch (error) {
      console.error('Error fetching audio text:', error);
      setAudioTexts(prev => ({
        ...prev,
        [fileId]: 'Error loading transcription. Please try again.'
      }));
    } finally {
      setIsLoadingText(false);
    }
  };

  const handlePreview = async (fileId, mimeType) => {
    if (!user) return;
    
    if (previewFileId === fileId) {
      setPreviewFileId(null);
      setCurrentPlayingFile(null);
      setIsManualPlay(false);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    } else {
      setPreviewFileId(fileId);
      if (mimeType.startsWith('audio/')) {
        try {
          // Set states first
          setCurrentPlayingFile(fileId);
          setIsManualPlay(true);
          setCurrentRepetition(1);
          setIsLoadingText(true);

          // Fetch audio text
          const textResponse = await fetch(`https://gnrl-bknd-server.onrender.com/api/audio-text/${fileId}`);
          if (textResponse.ok) {
            const textData = await textResponse.json();
            setAudioTexts(prev => ({
              ...prev,
              [fileId]: textData.text || 'No transcription available.'
            }));
          }

          // Fetch audio file
          const audioSource = `https://gnrl-bknd-server.onrender.com/api/getAudioFile?fileId=${fileId}`;
          const response = await fetch(audioSource);
          if (!response.ok) throw new Error(`Failed to fetch audio: ${response.statusText}`);
          const audioBlob = await response.blob();
          const audioObjectUrl = URL.createObjectURL(audioBlob);
          setAudioUrl(audioObjectUrl);
        } catch (error) {
          console.error('Error fetching audio or text:', error);
          setError(error.message);
        } finally {
          setIsLoadingText(false);
        }
      }
    }
  };

  const handleFolderClick = async (folderId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Toggle folder expansion
      setExpandedFolders(prev => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(folderId)) {
          newExpanded.delete(folderId);
        } else {
          newExpanded.add(folderId);
        }
        return newExpanded;
      });

      // Update current folder and history
      setFolderHistory(prev => [...prev, currentFolderId]);
      setCurrentFolderId(folderId);
    } catch (error) {
      console.error('Error handling folder click:', error);
      setError('Failed to open folder');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    const previousFolderId = folderHistory.pop();
    if (previousFolderId) {
      setCurrentFolderId(previousFolderId);
      setFolderHistory([...folderHistory]);
    }
  };

  const renderProgress = (file) => {
    if (!file.mimeType.startsWith('audio/')) return null;
    
    const progress = progressData[file.id];
    if (!progress) return null;

    return (
      <div className="progress-indicator">
        {progress.completed && <span className="completed-badge">✓</span>}
        <span className="progress-stats">
          {progress.repeats > 0 && `${progress.repeats}x `}
          {progress.playTime > 0 && `${Math.round(progress.playTime)}s`}
        </span>
      </div>
    );
  };

  const handleStart = () => {
    // Implementation of handleStart
  };

  const handlePause = () => {
    // Implementation of handlePause
  };

  const handleBatchAudioChange = async (currentFile) => {
    if (!currentFile) {
      setCurrentPlayingFile(null);
      setIsManualPlay(false);
      // Clear the text when no file is playing
      setAudioTexts(prev => ({
        ...prev,
        current: null
      }));
      return;
    }

    try {
      setCurrentPlayingFile(currentFile.id);
      setIsManualPlay(false);
      
      // Add to queue if not already fetched
      if (!audioTexts[currentFile.id]) {
        await fetchAudioText(currentFile.id);
      }
    } catch (error) {
      console.error('Error in batch audio change:', error);
    }
  };

  useEffect(() => {
    const processQueue = async () => {
      if (textFetchQueue.length > 0 && !isLoadingText) {
        const fileId = textFetchQueue[0];
        await fetchAudioText(fileId);
        setTextFetchQueue(prev => prev.slice(1));
      }
    };

    processQueue();
  }, [textFetchQueue, isLoadingText]);

  const handlePrevious = async () => {
    const audioFiles = files.filter(f => f.mimeType.startsWith('audio/'));
    const currentIndex = audioFiles.findIndex(f => f.id === currentPlayingFile);
    if (currentIndex > 0) {
      const prevFile = audioFiles[currentIndex - 1];
      await handlePreview(prevFile.id, prevFile.mimeType);
      setCurrentFileIndex(currentIndex - 1);
    }
  };

  const handleNext = async () => {
    const audioFiles = files.filter(f => f.mimeType.startsWith('audio/'));
    const currentIndex = audioFiles.findIndex(f => f.id === currentPlayingFile);
    if (currentIndex < audioFiles.length - 1) {
      const nextFile = audioFiles[currentIndex + 1];
      await handlePreview(nextFile.id, nextFile.mimeType);
      setCurrentFileIndex(currentIndex + 1);
    }
  };

  // Update the file list rendering to show folder structure
  const renderFileList = (files) => {
    return files
      .sort((a, b) => {
        // Sort folders first, then files
        if (a.mimeType === 'application/vnd.google-apps.folder' && 
            b.mimeType !== 'application/vnd.google-apps.folder') return -1;
        if (a.mimeType !== 'application/vnd.google-apps.folder' && 
            b.mimeType === 'application/vnd.google-apps.folder') return 1;
        return a.name.localeCompare(b.name);
      })
      .filter(file => !file.name.endsWith('.txt'))
      .map((file) => (
        <li key={file.id} className={`file-item ${expandedFolders.has(file.id) ? 'expanded' : ''}`}>
          <button 
            onClick={() => 
              file.mimeType === 'application/vnd.google-apps.folder' 
                ? handleFolderClick(file.id) 
                : handlePreview(file.id, file.mimeType)
            }
            className={`file-button ${
              file.mimeType === 'application/vnd.google-apps.folder' ? 'folder' : ''
            }`}
          >
            <span className="icon">
              {file.mimeType === 'application/vnd.google-apps.folder' ? '📁' : 
               file.mimeType.startsWith('audio/') ? '🎵' : '📄'}
            </span>
            <span className="filename">{file.name}</span>
            {renderProgress(file)}
            {file.mimeType === 'application/vnd.google-apps.folder' && (
              <span className="expand-icon">
                {expandedFolders.has(file.id) ? '▼' : '▶'}
              </span>
            )}
          </button>
          {previewFileId === file.id && (
            <div className="preview-container">
              {file.mimeType.startsWith('audio/') ? (
                <audio 
                  ref={audioRef}
                  src={audioUrl}
                  controls
                  className="audio-player"
                />
              ) : null}
            </div>
          )}
        </li>
      ));
  };

  const renderPlaybackControls = () => (
    <div className="playback-controls-sidebar">
      <div className="playback-controls-header">
        Playback Settings
      </div>
      
      {/* Voice Recorder Section */}
      <div className="voice-recorder-section">
        <div className="voice-recorder-controls">
          <div className="recorder-buttons">
            {!isRecording && !tempAudioUrl && (
              <button 
                className="record-button"
                onClick={startRecording}
                title="Start Recording"
              >
                🎙️
              </button>
            )}
            {isRecording && (
              <button 
                className="stop-button"
                onClick={stopRecording}
                title="Stop Recording"
              >
                ⏹️
              </button>
            )}
          </div>
          {tempAudioUrl && (
            <div className="recorded-audio-container">
              <audio 
                src={tempAudioUrl} 
                controls 
                className="temp-audio-player"
              />
              <button 
                className="reset-button"
                onClick={resetRecording}
                title="Reset Recording"
              >
                🔄
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Existing Playback Controls */}
      <div className="control-item">
        <span className="control-label">
          <span className="control-icon">🔄</span>
          Repeat Count
        </span>
        <div className="control-buttons">
          <button
            onClick={() => setRepeats(prev => Math.max(1, prev - 1))}
            className="control-button"
            disabled={repeats <= 1}
          >
            -
          </button>
          <span className="control-value">{repeats}x</span>
          <button
            onClick={() => setRepeats(prev => prev + 1)}
            className="control-button"
          >
            +
          </button>
        </div>
      </div>
      
      <div className="control-item">
        <span className="control-label">
          <span className="control-icon">⏱️</span>
          Gap Duration
        </span>
        <div className="control-buttons">
          <button
            onClick={() => setAudioGap(prev => Math.max(0, prev - 1))}
            className="control-button"
            disabled={audioGap <= 0}
          >
            -
          </button>
          <span className="control-value">{audioGap}s</span>
          <button
            onClick={() => setAudioGap(prev => prev + 1)}
            className="control-button"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );

  const renderFolderStats = () => (
    <div className="folder-stats">
      <div className="stat-item">
        <div className="stat-left">
          <span className="stat-icon">📁</span>
          <span className="stat-label">Folders</span>
        </div>
        <span className="stat-value">{folderStats.folderCount}</span>
      </div>
      <div className="stat-item">
        <div className="stat-left">
          <span className="stat-icon">🎵</span>
          <span className="stat-label">Audio Files</span>
        </div>
        <span className="stat-value">{folderStats.audioCount}</span>
      </div>
      {/* <div className="stat-item">
        <div className="stat-left">
          <span className="stat-icon">📊</span>
          <span className="stat-label">Total Items</span>
        </div>
        <span className="stat-value">{folderStats.totalFiles}</span>
      </div> */}
    </div>
  );

  // Add this effect to run cleanup when component mounts
  useEffect(() => {
    const initializeProgress = async () => {
      if (!user || !files.length) return;

      try {
        // Run cleanup first
        await fetch('https://gnrl-bknd-server.onrender.com/api/progress/cleanup', {
          method: 'POST'
        });

        // Then fetch progress for all audio files
        const audioFiles = files.filter(f => f.mimeType.startsWith('audio/'));
        await Promise.all(audioFiles.map(file => fetchProgress(file.id)));
      } catch (error) {
        console.error('Error initializing progress:', error);
      }
    };

    initializeProgress();
  }, [user, files]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setTempAudioUrl(url);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const resetRecording = () => {
    if (tempAudioUrl) {
      URL.revokeObjectURL(tempAudioUrl);
    }
    setAudioBlob(null);
    setTempAudioUrl(null);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="file-explorer">
      <h1 className="title">Course Contents</h1>
      
      {folderHistory.length > 0 && (
        <button className="back-button" onClick={handleGoBack}>
          ← Go Back
        </button>
      )}
      
      {renderFolderStats()}
      {user && renderPlaybackControls()}
      
      <div className="audio-transcription-panel">
        <div className="transcription-header">
          <h2>
            <span className="transcription-icon">🎧</span>
            Audio Transcription
          </h2>
          {currentPlayingFile && (
            <div className="now-playing">
              Now Playing: {files.find(f => f.id === currentPlayingFile)?.name}
            </div>
          )}
        </div>
        
        <div className="transcription-content">
          {isLoadingText ? (
            <div className="loading-text">
              <div className="loading-spinner"></div>
              Loading transcription...
            </div>
          ) : currentPlayingFile ? (
            <div className="current-audio-text">
              <p>{audioTexts[currentPlayingFile] || 'Loading text...'}</p>
            </div>
          ) : (
            <div className="no-audio-message">
              <span className="info-icon">ℹ️</span>
              Select an audio file to view its transcription
            </div>
          )}
        </div>
      </div>

      {user && (
        <BatchAudioPlayer
          files={files.filter(f => f.mimeType.startsWith('audio/'))}
          user={user}
          onUpdateProgress={updateProgress}
          progressData={progressData}
          onAudioChange={handleBatchAudioChange}
          isManualPlay={isManualPlay}
          currentPlayingFile={currentPlayingFile}
          audioGap={audioGap}
          repeats={repeats}
        />
      )}
    
      <ul className="file-list">
        {renderFileList(files)}
      </ul>
    </div>
  );
};

export default FileList;
